import { DocumentSnapshot, Timestamp, logger } from "../firestoreImports"
import { z } from "zod"
import { Car, EngineType } from "./car"

const fleetCarSchema = z.object({
    title: z.string().min(1),
    plateNum: z.string().min(1),
    timestampCreated: z.custom<Timestamp>().nullish(),
    engineType: z.enum(EngineType),
    note: z.string().nullish(),
    odometer: z.number().nonnegative(),
    deleted: z.boolean().default(false)
})

interface IFleetCar {
    id: string
    title: string
    plateNum: string
    timestampCreated?: Date
    engineType: EngineType
    note?: string
    odometer: number
    deleted?: boolean
}

export interface FleetCar extends IFleetCar {}
export class FleetCar {
    constructor(params: IFleetCar) {
        Object.assign(this, params)
    }

    static fromData(doc: DocumentSnapshot): FleetCar | null {
        const raw = doc.data()
        if (!raw) {
            logger.warn(`Cannot create FleetCar ${doc.id} from snapshot: missing data`)
            return null
        }

        try {
            const parsed = fleetCarSchema.parse(raw)
            return new FleetCar({
                id: doc.id,
                title: parsed.title,
                plateNum: parsed.plateNum,
                timestampCreated: parsed.timestampCreated?.toDate() ?? undefined,
                engineType: parsed.engineType,
                note: parsed.note ?? undefined,
                odometer: parsed.odometer,
                deleted: parsed.deleted ?? false
            })
        } catch (error) {
            logger.error(`Cannot create FleetCar ${doc.id} from snapshot:`, error)
            return null
        }
    }

    asData(): { [k: string]: any } {
        return fleetCarSchema.parse({
            title: this.title,
            plateNum: typeof this.plateNum === "string" ? Car.normalizePlate(this.plateNum) : this.plateNum,
            timestampCreated: this.timestampCreated ?? Timestamp.now(),
            engineType: this.engineType,
            note: this.note,
            odometer: this.odometer,
            deleted: this.deleted ?? false
        })
    }

    toString(): string {
        return `${this.title} (${this.plateNum})`
    }
}
